import { Field } from '../../libs/gs-component-form';
import { FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import { Field as FormikField, Formik } from 'formik';
import React, { useEffect, useReducer } from 'react';
import * as Yup from 'yup';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { H2 } from '@xo-union/tk-ui-typography';

import { useWeddingWebsiteQuery, WeddingWebsiteQuery } from '../../generated/graphql';
import { Footer } from '../FormComponents';
import styles from './index.scss';

const validationSchema = Yup.object().shape({
  domainName: Yup.string()
    .matches(/^(\d|[a-z0-9]|-)+$/, 'Try a domain using only lower case letters or numbers.')
    .max(52, 'Domain Name cannot exceed 52 characters.')
    .required('Domain Name cannot be blank.'),
  tld: Yup.string().required(),
});

interface InitialValues {
  domainName: string;
  tld: string;
}

interface Props {
  onSubmit: (domain: string) => void;
  loading: boolean;
  noMatch: boolean;
}

const initialValues: InitialValues = {
  domainName: '',
  tld: 'wedding',
};

const generateInspiration = (data: WeddingWebsiteQuery | undefined) => {
  let name = 'Jack';
  let fianceName = 'Jill';
  let weddingYear = new Date().getFullYear().toString();

  if (data?.wedding) {
    weddingYear = data?.wedding?.weddingDate?.substring(0, 4) || weddingYear;
  }
  if (data?.member) {
    name = data?.member?.firstName || name;
    fianceName = data?.member?.fianceFirstName || fianceName;
  }

  name = name.toLowerCase();
  fianceName = fianceName.toLowerCase();

  return [
    `${name}and${fianceName}`,
    `${name}and${fianceName}${weddingYear}`,
    `${name}and${fianceName}forever`,
    `${name}and${fianceName}wedding`,
    `${name}-and-${fianceName}`,
    `${name}and${fianceName}tietheknot`,
    `${name}and${fianceName}inlove`,
    `${name}and${fianceName}sayido`,
    `${name}and${fianceName}4ever`,
    `${name}and${fianceName}getmarried`,
  ];
};

interface FormError {
  [K: string]: string;
}

interface State {
  inspiration: string[];
  domainOptions: InitialValues;
  errors?: FormError;
}

type Payload = string[] | InitialValues | boolean;

const initialState: State = {
  inspiration: generateInspiration(undefined),
  domainOptions: initialValues,
};

const unmatchedError: FormError = {
  domainName: 'Sorry, that domain is unavailable. Try another one.',
};

const reducer = (state: State, action: { type: string; payload?: Payload }): State => {
  switch (action.type) {
    case 'updateDomainOptions':
      return {
        ...state,
        domainOptions: action.payload || initialValues,
        errors: undefined,
      } as State;
    case 'setNoMatch':
      console.log('setNoMatch', action.payload, state);
      return {
        ...state,
        errors: action.payload ? unmatchedError : undefined,
      } as State;
    case 'resetInspiration':
      return {
        ...state,
        inspiration: action.payload,
      } as State;
    case 'inspire':
      const [nextInspiration, ...remainingInspirations] = state.inspiration;
      return {
        domainOptions: {
          ...state.domainOptions,
          domainName: nextInspiration,
        },
        inspiration: [...remainingInspirations, nextInspiration],
      } as State;
    default:
      throw new Error();
  }
};

const Search: React.FC<Props> = ({ onSubmit, loading, noMatch }) => {
  const { loading: inspiring, data } = useWeddingWebsiteQuery();
  const [{ domainOptions, errors }, dispatch] = useReducer(reducer, initialState);
  const mobileMedia = useMobileMedia();

  useEffect(() => {
    dispatch({
      type: 'setNoMatch',
      payload: !loading && noMatch,
    });
  }, [loading, noMatch]);

  useEffect(() => {
    dispatch({
      type: 'resetInspiration',
      payload: generateInspiration(data),
    });
  }, [data]);

  return (
    <>
      <div className={styles.header}>
        <H2 as="h1">Search for a custom domain</H2>
      </div>
      <div className={styles.subheader}>Type something short and sweet to see if it’s available.</div>
      <div className={styles.formBody}>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={domainOptions}
          onSubmit={({ domainName, tld }: InitialValues) => onSubmit(`${domainName}.${tld}`)}
          initialErrors={errors}
        >
          {({ submitForm, touched, errors }): JSX.Element => {
            return (
              <>
                <FormRow>
                  <FormColumn xs="12" md="12">
                    <FormikField
                      name="domainName"
                      component={Field}
                      label="Domain"
                      value={domainOptions.domainName.trim()}
                      data-testid="domainNameField"
                      onChange={(evt: React.FormEvent<HTMLInputElement>): void =>
                        dispatch({
                          type: 'updateDomainOptions',
                          payload: { ...domainOptions, domainName: evt.currentTarget.value },
                        })
                      }
                    />
                  </FormColumn>
                </FormRow>
                <Footer
                  primaryOnClick={submitForm}
                  primaryText={'Check availability'}
                  secondaryOnClick={() => dispatch({ type: 'inspire' })}
                  secondaryText="Generate option"
                  showSpinner={loading || inspiring}
                  className={styles.footerOverride}
                  footerContainerClassOverride={styles.searchFooterContainerClassOverride}
                  buttonsSize={mobileMedia.yes ? 'sm' : 'lg'}
                  primaryDisabled={!Boolean(domainOptions.domainName)}
                />
              </>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default Search;
